import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navigation from '../components/Navigation';
import {
    MetricsService,
    centsToPesos,
    formatRatio,
    LINE_LABELS,
    METHOD_LABELS,
} from '../services/metricsService';
import { ILS } from '../utils/salesUtils';
import '../styles/pages/reports.css';

const MONTH_NAMES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const iso = (d) => [d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'), String(d.getDate()).padStart(2, '0')].join('-');

/**
 * The day before a 'YYYY-MM-DD' string, i.e. the inclusive last day of a
 * half-open range ending there. Pure UTC arithmetic so no timezone offset can
 * shift it, unlike parsing the string into a local Date.
 */
function dayBefore(dateStr) {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d - 1)).toISOString().slice(0, 10);
}

/** The earlier of a range's end and today: no report can describe the future. */
function effectiveAsOf(range, today = new Date()) {
    const lastDay = dayBefore(range.to);
    const todayStr = iso(today);
    return lastDay < todayStr ? lastDay : todayStr;
}

/** How many earlier months the picker offers. */
const PREVIOUS_MONTHS = 18;

/**
 * Ranges are half-open [from, to) to match the API, so `to` is always the first
 * day *after* the period. Built with `new Date(y, m, d)` so the month and week
 * boundaries follow the local calendar; the day-number overflow is intentional
 * (`new Date(2026, 0, 32)` is Feb 1), which is what makes month-end and
 * year-end arithmetic work without special cases.
 *
 * Every range uses day granularity: the widest of them is one calendar month,
 * well inside the API's 92-day cap for daily buckets.
 */
function buildRanges(today = new Date()) {
    const y = today.getFullYear();
    const m = today.getMonth();
    const d = today.getDate();

    // ISO weeks start on Monday, matching how the API keys its week buckets.
    const mondayOffset = (today.getDay() + 6) % 7; // Mon = 0 … Sun = 6
    const monday = new Date(y, m, d - mondayOffset);

    const ranges = {
        today: {
            label: 'Hoy',
            from: iso(new Date(y, m, d)),
            to: iso(new Date(y, m, d + 1)),
            granularity: 'day',
        },
        week: {
            label: 'Esta semana',
            from: iso(monday),
            to: iso(new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 7)),
            granularity: 'day',
        },
        month: {
            label: 'Este mes',
            from: iso(new Date(y, m, 1)),
            to: iso(new Date(y, m + 1, 1)),
            granularity: 'day',
        },
    };

    const months = [];
    for (let i = 1; i <= PREVIOUS_MONTHS; i += 1) {
        const start = new Date(y, m - i, 1);
        const key = `month:${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}`;
        ranges[key] = {
            label: `${MONTH_NAMES[start.getMonth()]} ${start.getFullYear()}`,
            from: iso(start),
            to: iso(new Date(start.getFullYear(), start.getMonth() + 1, 1)),
            granularity: 'day',
        };
        months.push(key);
    }

    return { ranges, quickKeys: ['today', 'week', 'month'], monthKeys: months };
}

/** '2026-10' → 'Octubre 2026'; '2026-10-11' → '11 oct'; '2026-W41' unchanged. */
function periodLabel(period) {
    const month = /^(\d{4})-(\d{2})$/.exec(period);
    if (month) return `${MONTH_NAMES[Number(month[2]) - 1]} ${month[1]}`;
    const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(period);
    if (day) return `${Number(day[3])} ${MONTH_NAMES[Number(day[2]) - 1].slice(0, 3).toLowerCase()}`;
    const week = /^(\d{4})-W(\d{2})$/.exec(period);
    if (week) return `Sem ${Number(week[2])}`;
    return period;
}

const money = (cents) => ILS(centsToPesos(cents));

/** A number the system cannot know yet reads as '—', never as zero. */
function Tile({ label, value, hint, tone = '', hintStrong = false }) {
    return (
        <div className={`rp-tile ${tone}`}>
            <span className="rp-tile-label">{label}</span>
            <span className="rp-tile-value">{value}</span>
            {/* hintStrong is opt-in per tile: most hints are quiet asides, but a
                few change how the number above them should be read. */}
            {hint ? <span className={`rp-tile-hint${hintStrong ? ' rp-tile-hint--strong' : ''}`}>{hint}</span> : null}
        </div>
    );
}

function BarRow({ label, value, max, display }) {
    const pct = max > 0 ? Math.max(value > 0 ? 1 : 0, Math.round((value / max) * 100)) : 0;
    return (
        <div className="rp-bar-row">
            <span className="rp-bar-label">{label}</span>
            <div className="rp-bar-track">
                <div className="rp-bar-fill" style={{ width: `${pct}%` }} />
            </div>
            <span className="rp-bar-value">{display}</span>
        </div>
    );
}

export default function Reports() {
    const { authorizedFetch, role } = useAuth();
    const navigate = useNavigate();

    const { ranges, quickKeys, monthKeys } = useMemo(() => buildRanges(), []);
    const [rangeKey, setRangeKey] = useState('month');
    const range = ranges[rangeKey];

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        // Non-admins render the locked notice instead; firing the requests would
        // only collect a handful of 403s.
        if (role !== 'admin') { setLoading(false); return; }
        setLoading(true);
        setError(null);
        try {
            const { from, to, granularity } = range;
            // Retention is an as-of metric, so it tracks the end of whatever
            // period is on screen — viewing March must not report today's churn.
            const asOf = effectiveAsOf(range);
            const [summary, revenue, payments, liability, retention] = await Promise.all([
                MetricsService.getSummary({ from, to }, authorizedFetch),
                MetricsService.getRevenue({ from, to, granularity }, authorizedFetch),
                MetricsService.getPayments({ from, to, granularity }, authorizedFetch),
                // Liability stays as-of-today: unused package units come from the
                // package's current status, which cannot be rewound.
                MetricsService.getLiability({}, authorizedFetch),
                MetricsService.getRetention({ asOf, period: 'month' }, authorizedFetch),
            ]);
            setData({ summary, revenue, payments, liability, retention });
        } catch (err) {
            console.error('Error loading metrics:', err);
            setError(err);
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [range, authorizedFetch, role]);

    useEffect(() => { load(); }, [load]);

    const lineTotals = useMemo(() => {
        if (!data) return [];
        const { revenue } = data;
        return revenue.lines
            .map(line => ({
                line,
                label: LINE_LABELS[line] || line,
                netCents: revenue.buckets.reduce((s, b) => s + b.lines[line].netCents, 0),
            }))
            .sort((a, b) => b.netCents - a.netCents);
    }, [data]);

    // After every hook: an early return above them would change the hook order
    // between renders.
    if (role !== 'admin') {
        return (
            <>
                <Navigation />
                <div className="rp-container">
                    <div className="rp-empty">
                        <span className="rp-empty-icon">🔒</span>
                        <h2>Solo administradores</h2>
                        <p>Los reportes de ingresos están restringidos a cuentas de administrador.</p>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <Navigation />
            <div className="rp-container">

                <div className="rp-header">
                    <div>
                        <button className="rp-back" onClick={() => navigate('/settings')}>‹ Configuración</button>
                        <h1 className="rp-title">Reportes y analíticas</h1>
                        <p className="rp-subtitle">
                            Ingresos reconocidos en la fecha de venta, en pesos sin IVA declarado.
                        </p>
                    </div>
                    {data ? (
                        <span className="rp-fresh">
                            Datos hasta {data.summary.dataThrough}
                        </span>
                    ) : null}
                </div>

                <div className="rp-presets">
                    {quickKeys.map(key => (
                        <button
                            key={key}
                            className={`rp-preset${key === rangeKey ? ' rp-preset--on' : ''}`}
                            onClick={() => setRangeKey(key)}
                        >
                            {ranges[key].label}
                        </button>
                    ))}

                    {/* A select rather than more chips: 18 months of buttons would
                        bury the three ranges people actually use daily. */}
                    <select
                        className={`rp-preset rp-monthpick${rangeKey.startsWith('month:') ? ' rp-preset--on' : ''}`}
                        value={rangeKey.startsWith('month:') ? rangeKey : ''}
                        onChange={(e) => { if (e.target.value) setRangeKey(e.target.value); }}
                        aria-label="Elegir un mes anterior"
                    >
                        <option value="">Meses anteriores…</option>
                        {monthKeys.map(key => (
                            <option key={key} value={key}>{ranges[key].label}</option>
                        ))}
                    </select>
                </div>

                <p className="rp-rangeline">
                    {range.label}
                    <span className="rp-rangeline-dates">
                        {range.from === dayBefore(range.to)
                            ? range.from
                            : `${range.from} al ${dayBefore(range.to)}`}
                    </span>
                </p>

                {loading ? <div className="rp-loading">Calculando…</div> : null}

                {error ? (
                    <div className="rp-error">
                        <strong>No se pudieron cargar los reportes.</strong>
                        <span>{error.message}</span>
                        {error.code && error.code !== 'UNKNOWN'
                            ? <code className="rp-error-code">{error.code}{error.field ? ` · ${error.field}` : ''}</code>
                            : null}
                        <button className="rp-retry" onClick={load}>Reintentar</button>
                    </div>
                ) : null}

                {data && !loading ? (
                    <>
                        {/* Revenue */}
                        <div className="rp-section">
                            <p className="rp-section-label">Ingresos</p>
                            <div className="rp-tiles">
                                <Tile label="Neto" value={money(data.summary.revenue.netCents)} tone="rp-tile--primary" />
                                <Tile label="Bruto" value={money(data.summary.revenue.grossCents)} />
                                <Tile label="Descuentos" value={money(data.summary.revenue.discountCents)} />
                                <Tile label="Cashback usado" value={money(data.summary.revenue.cashbackCents)} />
                            </div>
                        </div>

                        {/* Per line */}
                        <div className="rp-section">
                            <p className="rp-section-label">Neto por servicio</p>
                            <div className="rp-card">
                                {lineTotals.some(l => l.netCents !== 0) ? lineTotals.map(l => (
                                    <BarRow
                                        key={l.line}
                                        label={l.label}
                                        value={l.netCents}
                                        max={Math.max(...lineTotals.map(x => x.netCents), 0)}
                                        display={money(l.netCents)}
                                    />
                                )) : <p className="rp-none">Sin ventas en este periodo.</p>}
                            </div>
                        </div>

                        {/* Over time — pointless for a single-day range */}
                        {data.revenue.buckets.length > 1 ? (
                            <div className="rp-section">
                                <p className="rp-section-label">
                                    Neto por día
                                </p>
                                <div className="rp-card">
                                    <TimeChart
                                        buckets={data.revenue.buckets}
                                        lines={data.revenue.lines}
                                        dataThrough={data.revenue.dataThrough}
                                    />
                                </div>
                            </div>
                        ) : null}

                        {/* Collections */}
                        <div className="rp-section">
                            <p className="rp-section-label">Cobranza</p>
                            <div className="rp-tiles">
                                <Tile label="Cobrado" value={money(data.payments.collectedCents)} />
                                <Tile
                                    label="Por cobrar"
                                    value={money(data.payments.outstandingCents)}
                                    hint="Todas las ventas abiertas, no sólo del periodo"
                                    hintStrong
                                    tone={data.payments.outstandingCents > 0 ? 'rp-tile--warn' : ''}
                                />
                            </div>
                            <div className="rp-card">
                                {Object.entries(data.payments.totals).some(([, v]) => v !== 0)
                                    ? Object.entries(data.payments.totals)
                                        .sort((a, b) => b[1] - a[1])
                                        .map(([method, cents]) => (
                                            <BarRow
                                                key={method}
                                                label={METHOD_LABELS[method] || method}
                                                value={cents}
                                                max={Math.max(...Object.values(data.payments.totals), 0)}
                                                display={money(cents)}
                                            />
                                        ))
                                    : <p className="rp-none">Sin pagos registrados en este periodo.</p>}
                            </div>
                        </div>

                        {/* Operations */}
                        <div className="rp-section">
                            <p className="rp-section-label">Operación</p>
                            <div className="rp-tiles">
                                <Tile
                                    label="Noches de hotel"
                                    value={data.summary.hotel.occupiedNights}
                                    hint={`${data.summary.hotel.staysStarted} estancias · promedio ${data.summary.hotel.alosNights ?? '—'} noches`}
                                />
                                <Tile
                                    label="Ocupación"
                                    value="—"
                                    hint="Falta registrar capacidad de kenneles"
                                    tone="rp-tile--muted"
                                />
                                <Tile
                                    label="Días-perro guardería"
                                    value={data.summary.daycare.attendedDogDays}
                                    hint={`${data.summary.daycare.operatingDays} días abiertos`}
                                />
                                <Tile
                                    label="Utilización guardería"
                                    value={formatRatio(data.summary.daycare.utilization)}
                                    hint={`Pico ${formatRatio(data.summary.daycare.peakUtilization)} · cap. ${data.summary.daycare.dailyCapacity}/día`}
                                />
                                <Tile
                                    label="Estéticas"
                                    value={`${data.summary.grooming.completed} / ${data.summary.grooming.bookings}`}
                                    hint={`Completadas ${formatRatio(data.summary.grooming.completionRatio)}`}
                                />
                            </div>
                        </div>

                        {/* Clients */}
                        <div className="rp-section">
                            <p className="rp-section-label">Clientes</p>
                            <div className="rp-tiles">
                                <Tile
                                    label="Clientes activos"
                                    value={data.summary.clients.active}
                                    hint={`Con servicio en los últimos ${data.summary.clients.windowDays} días`}
                                />
                                <Tile
                                    label="Servicios por cliente"
                                    value={data.summary.clients.avgLinesPerClient ?? '—'}
                                    hint="Promedio de líneas distintas"
                                />
                                <Tile
                                    label="Retención mensual"
                                    value={formatRatio(data.retention.retentionRatio)}
                                    hint={`${data.retention.retainedCount} de ${data.retention.cohortSize} del mes anterior`}
                                />
                                <Tile
                                    label="Abandono"
                                    value={formatRatio(data.retention.churnRatio)}
                                    hint={`${data.retention.churnedCount} sin volver · ${data.retention.newClients} nuevos`}
                                    tone={data.retention.churnRatio > 0.3 ? 'rp-tile--warn' : ''}
                                />
                            </div>
                            <p className="rp-footnote">
                                Ventanas móviles de {data.retention.spanDays} días terminando en {data.retention.asOf},
                                no meses de calendario.
                            </p>
                        </div>

                        {/* Liability */}
                        <div className="rp-section">
                            <p className="rp-section-label">
                                Pasivos
                                <span className="rp-section-note">al día de hoy</span>
                            </p>
                            <div className="rp-tiles">
                                <Tile
                                    label="Cashback por usar"
                                    value={money(data.liability.cashbackOutstandingCents)}
                                    hint="Puntos acumulados, 1 punto = $1"
                                />
                                <Tile
                                    label="Paquetes sin usar"
                                    value={`${data.liability.packagesOutstandingUnits.total} u.`}
                                    hint={`${data.liability.packagesOutstandingUnits.daycareDays} días · ${data.liability.packagesOutstandingUnits.baths} baños · ${data.liability.packagesOutstandingUnits.busTrips} viajes`}
                                />
                                {data.liability.packagesExpiredUnits.daycareDays > 0 ? (
                                    <Tile
                                        label="Días vencidos"
                                        value={`${data.liability.packagesExpiredUnits.daycareDays} u.`}
                                        hint="Pagados y ya no canjeables; fuera del pasivo"
                                        tone="rp-tile--warn"
                                    />
                                ) : null}
                                <Tile
                                    label="Valor de paquetes"
                                    value="—"
                                    hint="Los paquetes no guardan precio"
                                    tone="rp-tile--muted"
                                />
                            </div>
                        </div>

                        {/* Anomalies — only when there is something to say */}
                        {data.revenue.anomalies.salesWithoutItems > 0
                            || data.revenue.anomalies.reductionsClampedCents > 0
                            || data.liability.anomalies.expiredDaycarePackagesStillActive > 0 ? (
                            <div className="rp-section">
                                <p className="rp-section-label">Revisar</p>
                                <div className="rp-card rp-card--warn">
                                    <ul className="rp-list">
                                        {data.revenue.anomalies.salesWithoutItems > 0 ? (
                                            <li>
                                                {data.revenue.anomalies.salesWithoutItems} venta(s) con descuento o pago
                                                pero sin conceptos: su monto no se asignó a ningún servicio.
                                            </li>
                                        ) : null}
                                        {data.revenue.anomalies.reductionsClampedCents > 0 ? (
                                            <li>
                                                {money(data.revenue.anomalies.reductionsClampedCents)} en descuentos y
                                                cashback por encima del total de su venta; se ajustó al tope para no
                                                reportar ingresos negativos.
                                            </li>
                                        ) : null}
                                        {data.liability.anomalies.expiredDaycarePackagesStillActive > 0 ? (
                                            <li>
                                                {data.liability.anomalies.expiredDaycarePackagesStillActive} paquete(s)
                                                de guardería ya vencieron pero siguen marcados como activos. No se
                                                pueden canjear y sus días no cuentan como pasivo; sólo falta
                                                archivarlos. Se corrige al abrir{' '}
                                                <button className="rp-link" onClick={() => navigate('/daycare/packages/active')}>
                                                    Paquetes activos
                                                </button>.
                                            </li>
                                        ) : null}
                                    </ul>
                                </div>
                            </div>
                        ) : null}

                    </>
                ) : null}

            </div>
        </>
    );
}

/**
 * Net per bucket as vertical bars. Hand-drawn: the project has no chart
 * library, and one bar chart is not worth adding a dependency for.
 */
function TimeChart({ buckets, lines, dataThrough }) {
    const series = buckets
        // Days that have not happened yet come back zero-filled by design. Drawn,
        // they read as revenue falling off a cliff mid-week, so the chart stops
        // at the last day with data.
        .filter(b => !dataThrough || b.period <= dataThrough)
        .map(b => ({
            period: b.period,
            netCents: lines.reduce((s, l) => s + b.lines[l].netCents, 0),
        }));
    const max = Math.max(...series.map(s => s.netCents), 0);

    if (max <= 0) return <p className="rp-none">Sin ingresos en este periodo.</p>;

    return (
        <div className="rp-chart" role="img" aria-label="Ingreso neto por periodo">
            {series.map(s => (
                <div className="rp-chart-col" key={s.period}>
                    <div className="rp-chart-barwrap">
                        <div
                            className="rp-chart-bar"
                            style={{ height: `${Math.max(s.netCents > 0 ? 2 : 0, (s.netCents / max) * 100)}%` }}
                            title={`${periodLabel(s.period)}: ${money(s.netCents)}`}
                        />
                    </div>
                    <span className="rp-chart-tick">{periodLabel(s.period)}</span>
                </div>
            ))}
        </div>
    );
}
